import { movies, genres, collections } from '@/mocks/movies';
import type { Movie, Genre, Collection } from '@/types/movie';

export const allMovies = movies;
export const allGenres = genres;
export const allCollections = collections;

export function getGenre(key: string): Genre | undefined {
  return genres.find((g) => g.key === key);
}

export function getGenreName(key: string): string {
  return getGenre(key)?.name ?? key;
}

export function getGenreNames(keys: string[]): string {
  return keys.map((k) => getGenreName(k)).join(' / ');
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getCollection(key: string): Collection | undefined {
  return collections.find((c) => c.key === key);
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids
    .map((id) => getMovieById(id))
    .filter((m): m is Movie => Boolean(m));
}

export const REGIONS = ['大陆', '欧美', '日韩'];

export const YEAR_RANGES = [
  { key: 'all', label: '全部年份' },
  { key: '2023+', label: '2023 及以后' },
  { key: '2020-2022', label: '2020-2022' },
  { key: 'before-2020', label: '2020 以前' },
];

export const RATING_RANGES = [
  { key: 'all', label: '全部评分', min: 0 },
  { key: '8.5+', label: '8.5 分以上', min: 8.5 },
  { key: '8.0+', label: '8.0 分以上', min: 8.0 },
  { key: '7.5+', label: '7.5 分以上', min: 7.5 },
];

export const SORT_OPTIONS = [
  { key: 'latest', label: '最新上映' },
  { key: 'popular', label: '热门指数' },
  { key: 'rating', label: '最高评分' },
];

export interface MovieFilter {
  genre?: string;
  region?: string;
  year?: string;
  rating?: string;
  keyword?: string;
  sort?: string;
}

function matchYear(year: number, range?: string): boolean {
  if (!range || range === 'all') return true;
  if (range === '2023+') return year >= 2023;
  if (range === '2020-2022') return year >= 2020 && year <= 2022;
  if (range === 'before-2020') return year < 2020;
  return true;
}

function matchRating(rating: number, range?: string): boolean {
  if (!range || range === 'all') return true;
  const found = RATING_RANGES.find((r) => r.key === range);
  if (!found) return true;
  return rating >= found.min;
}

export function filterMovies(filter: MovieFilter): Movie[] {
  const { genre, region, year, rating, keyword, sort } = filter;
  const kw = (keyword ?? '').trim().toLowerCase();

  const result = movies.filter((m) => {
    if (genre && !m.genres.includes(genre)) return false;
    if (region && region !== 'all' && m.region !== region) return false;
    if (!matchYear(m.year, year)) return false;
    if (!matchRating(m.rating, rating)) return false;
    if (kw) {
      const haystack = `${m.title} ${m.enTitle} ${m.director} ${m.cast.join(' ')}`.toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });

  const sorted = [...result];
  if (sort === 'popular') {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else if (sort === 'rating') {
    sorted.sort((a, b) => b.rating - a.rating);
  } else {
    sorted.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  }
  return sorted;
}

export function getFeaturedMovies(count = 5): Movie[] {
  return [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, count);
}

export function getLatestMovies(count = 8): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year || b.popularity - a.popularity).slice(0, count);
}

export function getPopularMovies(count = 6): Movie[] {
  return [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, count);
}

export function getRelatedMovies(movie: Movie, count = 4): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const shared = m.genres.filter((g) => movie.genres.includes(g)).length;
      return { movie: m, score: shared * 10 + m.rating };
    })
    .filter((item) => item.score >= 10)
    .sort((a, b) => b.score - a.score);
  const pool = scored.length >= count ? scored : [...movies].filter((m) => m.id !== movie.id).map((m) => ({ movie: m, score: m.rating }));
  return pool.slice(0, count).map((item) => item.movie);
}